/**
 * The things a customer can do to a design.
 *
 * Kept out of the components so a keyboard shortcut, a menu item and a button
 * all reach the same code - and so each one commits with a label, which is what
 * makes the undo list readable rather than forty entries of "Change".
 */

import { useEditor } from './store';
import { extent, toUnit } from '../shapes/paths';
import { fromPath, rebase } from '../shapes/nodes';
import { shapeDef, shapePath } from '../shapes/library';
import { firstColour } from '../press/draw';
import type { Effect, ImageLayer, Layer, Page, PathNode, QrLayer, ShapeKind, TextLayer, TrimboxDocument } from '../model/types';
import { findLayer, isGroup, pageLabel, printedPages } from '../model/types';
import type { Preset } from '../effects/presets';
import { fontFamilies } from '../text/fonts';
import { layoutFor } from '../text/layout';
import { placeText } from '../text/place';
import { api, pagesFixed, signedIn } from '../api';
import { keepPicture } from '../local';
import { printableFile } from '../printable';
import { importSvg } from '../svg/import';
import { buildPayload, fieldsFor, type QrFieldValues, type QrKind } from '../codes/qr-types';
import { ensureStored } from './persist';
import { registerAssets, toDocumentRef, type Asset } from '../assets';
import { fillSelectedFrame } from './masks';

/** An id that cannot collide with one already in the document. */
export function newId(prefix: string): string {
  return `${prefix}${Math.random().toString(36).slice(2, 9)}`;
}

/** Everything a layer has before its type says anything more. */
function base(type: Layer['type'], name: string, page: Page) {
  // New objects land in the middle of the page rather than at 0,0. An object
  // that appears in the corner reads as a mistake.
  const w = Math.min(page.size.w * 0.6, 60);
  const h = type === 'text' ? 10 : Math.min(page.size.h * 0.4, 30);

  return {
    id: newId(type[0] ?? 'l'),
    type,
    name,
    x: Math.round((page.size.w - w) / 2),
    y: Math.round((page.size.h - h) / 2),
    w,
    h,
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
  };
}

const BLACK = { space: 'cmyk' as const, value: [0, 0, 0, 1] as [number, number, number, number] };

export function addText(text = 'Your text here') {
  const store = useEditor.getState();
  const page = store.page();

  if (!page) return;

  const layer: Layer = {
    ...base('text', 'Text', page),
    type: 'text',
    text,
    font: { family: firstFamily(), weight: 400, style: 'normal', size: 11 },
    direction: 'auto',
    align: 'left',
    lineHeight: 1.2,
    letterSpacing: 0,
    underline: false,
    strike: false,
    fill: BLACK,
    stroke: null,
  };

  place(layer, 'Add text');
}

export interface TextStyle {
  text: string;
  /** Points. */
  size: number;
  weight?: number;
  family?: string;
  align?: TextLayer['align'];
  letterSpacing?: number;
}

/**
 * A text in a given style - the heading, subheading and body the Text panel
 * offers - sized to the words rather than to a default box, and centred on the
 * page. Falls back to the shop's first family if the one asked for is missing.
 */
export function addTextStyle(style: TextStyle, label = 'Add text'): string | null {
  const store = useEditor.getState();
  const page = store.page();

  if (!page) return null;

  const layer = styledText(style, page);

  place(layer, label);

  return layer.id;
}

function styledText(style: TextStyle, page: Page, y?: number): TextLayer {
  const family = style.family && fontFamilies().includes(style.family) ? style.family : firstFamily();
  const sizeMm = style.size * (25.4 / 72);
  const h = round(sizeMm * 1.4);
  const w = round(Math.min(page.size.w * 0.86, Math.max(20, style.text.length * sizeMm * 0.62)));

  return {
    ...base('text', 'Text', page),
    type: 'text',
    name: style.text.slice(0, 40),
    x: round((page.size.w - w) / 2),
    y: round(y ?? (page.size.h - h) / 2),
    w,
    h,
    text: style.text,
    font: { family, weight: style.weight ?? 400, style: 'normal', size: style.size },
    direction: 'auto',
    align: style.align ?? 'center',
    lineHeight: 1.2,
    letterSpacing: style.letterSpacing ?? 0,
    underline: false,
    strike: false,
    fill: BLACK,
    stroke: null,
  };
}

/**
 * A font pairing: two or more lines set together and grouped, as the Text
 * panel's combinations are in Canva - one click puts down a heading with its
 * line underneath, already sized and spaced against each other.
 */
export function addTextCombo(lines: TextStyle[]) {
  const store = useEditor.getState();
  const page = store.page();

  if (!page || lines.length === 0) return;

  const heights = lines.map((line) => line.size * (25.4 / 72) * 1.4);
  const total = heights.reduce((sum, h) => sum + h, 0);
  let y = (page.size.h - total) / 2;

  const layers = lines.map((line, index) => {
    const layer = styledText(line, page, y);

    y += heights[index] ?? 0;

    return layer;
  });

  store.updatePage('Add text', (current) => ({ ...current, layers: current.layers.concat(layers) }));
  store.select(layers.map((layer) => layer.id));

  if (layers.length > 1) groupSelected();
}

/**
 * How many points or sides a shape wants when it is first placed.
 *
 * A star is five-pointed, a burst has twelve shallow ones, a polygon is a
 * hexagon. Getting these right matters more than it looks: a customer who
 * places a burst and sees a five-pointed star assumes the tool is broken
 * rather than that a number needs changing.
 */
const POINTS: Partial<Record<ShapeKind, number>> = {
  star: 5,
  burst: 12,
  seal: 12,
  polygon: 6,
};

export function addShape(shape: ShapeKind = 'rect') {
  const store = useEditor.getState();
  const page = store.page();

  if (!page) return;

  const start = base('shape', 'Shape', page);

  /*
   * Every shape's path is parametric - it fills whatever box it is handed - so
   * `ratio` is not something a shape has, it is how wide it should arrive.
   * Undeclared means square, because a circle, a pentagon and a star are all
   * wrong at anything else, and the box new objects land in is not square: on a
   * mug wrap it is 60 by 30, which turned every circle into an ellipse.
   *
   * The freehand shape keeps its own proportions, being whatever was drawn.
   */
  const wide = shape === 'free' ? start.w / start.h : shapeDef(shape).ratio ?? 1;

  // Fitted inside the box rather than overflowing it: keep the width when the
  // height allows, and fall back to the height when it does not, so a circle on
  // a shallow page is a smaller circle rather than one running off the page.
  let w = start.w;
  let h = w / wide;

  if (h > start.h) {
    h = start.h;
    w = h * wide;
  }

  const layer: Layer = {
    ...start,
    x: start.x + (start.w - w) / 2,
    y: start.y + (start.h - h) / 2,
    w,
    h,
    type: 'shape',
    shape,
    // Named for what it is, so the layers panel is readable at a glance.
    name: shapeDef(shape).name,
    fill: BLACK,
    stroke: null,
    radius: 0,
    points: POINTS[shape] ?? 5,
  };

  place(layer, 'Add shape');
}

/**
 * A QR code the customer has already said what to put in.
 *
 * The value is asked for before the code is added rather than after. A code
 * added with something plausible in it - the shop's own address, say - is a
 * code that can be laid out, approved and printed while still pointing at
 * whatever the placeholder was, and nobody notices until a customer scans a
 * card and lands somewhere they did not expect.
 */
export function addQr(kind: QrKind, answers: QrFieldValues) {
  const store = useEditor.getState();
  const page = store.page();
  const fields = fieldsFor(kind, answers);
  const value = buildPayload(kind, fields);

  // A code that holds nothing is not added: it would print, scan to nothing,
  // and nobody would know until a customer tried it.
  if (!page || !value) return;

  const side = Math.min(page.size.w, page.size.h) * 0.3;

  const layer: Layer = {
    ...base('qr', 'QR code', page),
    type: 'qr',
    // Square, always. A stretched QR code does not scan.
    w: side,
    h: side,
    kind,
    fields,
    value,
    ecc: 'M',
    fill: BLACK,
    background: { space: 'cmyk', value: [0, 0, 0, 0] },
  };

  place(layer, 'Add QR code');
}

/**
 * What a code holds, changed.
 *
 * The payload is rebuilt with the fields in the same step, so `value` - which
 * is what the server and preflight read to decide whether a code is empty -
 * can never describe a different code from the one that prints.
 */
export function setQrContent(id: string, kind: QrKind, answers: QrFieldValues) {
  const fields = fieldsFor(kind, answers);

  useEditor.getState().updateLayer(
    id,
    'Change QR code',
    (l) => ({ ...(l as QrLayer), kind, fields, value: buildPayload(kind, fields) }),
    { coalesce: `${id}:qr-content` }
  );
}

/**
 * Uploads a picture and puts it on the page.
 *
 * The box is sized to the picture's own shape rather than to a default
 * rectangle, so a landscape photo does not arrive looking cropped before the
 * customer has touched it.
 *
 * @returns The problem to show, or null when it worked.
 */
export async function addImage(given: File): Promise<string | null> {
  const store = useEditor.getState();
  const design = store.design;
  const page = store.page();

  if (!design || !page) return null;

  // WebP and GIF become JPEG or PNG first: the press file can only carry those.
  const file = await printableFile(given);

  let asset: Asset;

  try {
    /*
     * A visitor's picture stays in their browser with the rest of their work,
     * and goes to the shop when they sign in. A customer's is uploaded, to a
     * design created for it if this card has not been saved yet.
     */
    asset = signedIn() ? await api.upload(await ensureStored(), file) : await keepPicture(file);
  } catch (error) {
    return error instanceof Error ? error.message : 'That picture could not be uploaded.';
  }

  registerAssets([asset]);
  placeAsset(asset);

  return null;
}

/**
 * Puts an uploaded picture on the page - a fresh upload, or one from the
 * Uploads panel being used again. The same picture twice is one file on the
 * server and one image in the press file; only the layer is new.
 */
export function placeAsset(asset: Asset) {
  const store = useEditor.getState();
  const page = store.page();

  if (!page) return;

  // A frame is selected: the picture goes into it.
  if (fillSelectedFrame(asset)) return;

  // Two thirds of the page's width, or whatever keeps it inside the page.
  const ratio = asset.h > 0 ? asset.w / asset.h : 1;
  const width = Math.min(page.size.w * 0.66, page.size.h * 0.8 * ratio);
  const height = width / ratio;

  const layer: ImageLayer = {
    ...base('image', asset.name || 'Picture', page),
    type: 'image',
    w: round(width),
    h: round(height),
    x: round((page.size.w - width) / 2),
    y: round((page.size.h - height) / 2),
    asset: asset.id,
    fit: 'cover',
    crop: { x: 0, y: 0, w: 1, h: 1 },
    filters: { brightness: 0, contrast: 0, saturation: 0, greyscale: false },
  };

  const current = useEditor.getState();

  current.commit('Add picture', (doc) => {
    const target = doc.pages[current.pageIndex];

    if (!target) return doc;

    const pages = doc.pages.slice();
    pages[current.pageIndex] = { ...target, layers: target.layers.concat(layer) };

    // The document records the asset by id. The signed URL is runtime state and
    // would be stale by the time anyone reopened the design. Recorded once,
    // however many times it is placed.
    const known = doc.assets.some((ref) => ref.id === asset.id);

    return { ...doc, pages, assets: known ? doc.assets : doc.assets.concat(toDocumentRef(asset)) };
  });

  current.select([layer.id]);
}

function place(layer: Layer, label: string) {
  const store = useEditor.getState();

  store.updatePage(label, (page) => ({ ...page, layers: page.layers.concat(layer) }));
  store.select([layer.id]);
}

/**
 * The family a new text layer takes.
 *
 * The document's own first font, so a design keeps to the type it already
 * uses; Cairo when there is none, because it sets Arabic and Latin both and
 * this is a Doha print shop.
 */
export function firstFamily(): string {
  const doc = useEditor.getState().doc;

  return doc?.fonts[0]?.family ?? 'Cairo';
}

/* ---------------------------------------------------------------
 * Changing what is already there
 * ------------------------------------------------------------ */

export function deleteSelected() {
  const store = useEditor.getState();
  const ids = new Set(store.selection);

  if (ids.size === 0) return;

  store.updatePage(ids.size === 1 ? 'Delete object' : `Delete ${ids.size} objects`, (page) => ({
    ...page,
    layers: without(page.layers, ids),
  }));

  store.select([]);
}

export function duplicateSelected() {
  const store = useEditor.getState();
  const chosen = store.selected();

  if (chosen.length === 0) return;

  // Offset, so the copy is visible rather than hidden exactly behind the
  // original - the classic "nothing happened" bug.
  const copies = chosen.map((layer) => reId({ ...layer, x: layer.x + 3, y: layer.y + 3 }));

  store.updatePage('Duplicate', (page) => ({ ...page, layers: page.layers.concat(copies) }));
  store.select(copies.map((layer) => layer.id));
}

export function nudgeSelected(dx: number, dy: number) {
  const store = useEditor.getState();

  for (const layer of store.selected()) {
    if (layer.locked) continue;

    store.updateLayer(
      layer.id,
      'Move',
      (l) => ({ ...l, x: round(l.x + dx), y: round(l.y + dy) }),
      // One undo step for a held arrow key, not one per repeat.
      { coalesce: 'nudge' }
    );
  }
}

export function moveSelected(id: string, x: number, y: number) {
  useEditor.getState().updateLayer(id, 'Move', (l) => ({ ...l, x: round(x), y: round(y) }), {
    coalesce: `drag:${id}`,
  });
}

/** Sends the selection up or down the drawing order. */
export function reorderSelected(direction: 'up' | 'down' | 'front' | 'back') {
  const store = useEditor.getState();
  const ids = new Set(store.selection);

  if (ids.size === 0) return;

  store.updatePage(`Bring ${direction}`, (page) => {
    const staying = page.layers.filter((layer) => !ids.has(layer.id));
    const moving = page.layers.filter((layer) => ids.has(layer.id));

    if (moving.length === 0) return page;

    if (direction === 'front') return { ...page, layers: staying.concat(moving) };
    if (direction === 'back') return { ...page, layers: moving.concat(staying) };

    // One step at a time, which is what the bracket keys mean.
    const layers = page.layers.slice();
    const step = direction === 'up' ? 1 : -1;
    const order = direction === 'up' ? [...layers.keys()].reverse() : [...layers.keys()];

    for (const index of order) {
      const layer = layers[index];
      const target = index + step;

      if (!layer || !ids.has(layer.id) || target < 0 || target >= layers.length) continue;
      if (ids.has(layers[target]!.id)) continue;

      [layers[index], layers[target]] = [layers[target]!, layer];
    }

    return { ...page, layers };
  });
}

export function groupSelected() {
  const store = useEditor.getState();
  const ids = new Set(store.selection);

  if (ids.size < 2) return;

  const page = store.page();

  if (!page) return;

  const members = page.layers.filter((layer) => ids.has(layer.id));

  if (members.length < 2) return;

  const group: Layer = {
    id: newId('g'),
    type: 'group',
    name: 'Group',
    x: Math.min(...members.map((l) => l.x)),
    y: Math.min(...members.map((l) => l.y)),
    w: Math.max(...members.map((l) => l.x + l.w)) - Math.min(...members.map((l) => l.x)),
    h: Math.max(...members.map((l) => l.y + l.h)) - Math.min(...members.map((l) => l.y)),
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    children: members,
  };

  store.updatePage('Group', (p) => ({
    ...p,
    // The group takes the place of the topmost member, so grouping does not
    // also change what is in front of what.
    layers: p.layers.filter((layer) => !ids.has(layer.id)).concat(group),
  }));

  store.select([group.id]);
}

export function ungroupSelected() {
  const store = useEditor.getState();
  const chosen = store.selected().filter(isGroup);

  if (chosen.length === 0) return;

  const ids = new Set(chosen.map((group) => group.id));
  const freed = chosen.flatMap((group) => group.children);

  store.updatePage('Ungroup', (page) => ({
    ...page,
    layers: page.layers.flatMap((layer) => (ids.has(layer.id) && isGroup(layer) ? layer.children : [layer])),
  }));

  store.select(freed.map((layer) => layer.id));
}

export function toggleLock() {
  const store = useEditor.getState();

  for (const layer of store.selected()) {
    store.updateLayer(layer.id, layer.locked ? 'Unlock' : 'Lock', (l) => ({ ...l, locked: !l.locked }));
  }
}

/** Renames one layer. The name is the customer's, so it is kept as typed. */
export function renameLayer(id: string, name: string) {
  useEditor.getState().updateLayer(id, 'Rename', (layer) => ({ ...layer, name: name.slice(0, 120) }));
}

/** Hides or shows one layer. Hidden layers are not printed and not preflighted. */
export function toggleHidden(id: string) {
  const store = useEditor.getState();

  store.updateLayer(id, 'Show or hide', (layer) => ({ ...layer, hidden: !layer.hidden }));

  // A hidden layer cannot be selected, or the properties panel would be
  // describing something invisible.
  if (store.selection.includes(id)) store.select(store.selection.filter((other) => other !== id));
}

/** Locks or unlocks one layer, whatever is selected. */
export function toggleLocked(id: string) {
  useEditor.getState().updateLayer(id, 'Lock or unlock', (layer) => ({ ...layer, locked: !layer.locked }));
}

/**
 * Moves a layer to a given place in the drawing order.
 *
 * The index is into the document's own order - bottom first - even though the
 * layers panel shows the list the other way up. Translating between the two
 * happens in the panel, where the reversal is visible, rather than here.
 */
export function moveLayerTo(id: string, index: number) {
  const store = useEditor.getState();

  store.updatePage('Reorder', (page) => {
    const from = page.layers.findIndex((layer) => layer.id === id);

    if (from < 0 || from === index) return page;

    const layers = page.layers.slice();
    const [moved] = layers.splice(from, 1);

    if (!moved) return page;

    layers.splice(Math.max(0, Math.min(layers.length, index)), 0, moved);

    return { ...page, layers };
  });
}

export function selectAll() {
  const store = useEditor.getState();
  const page = store.page();

  if (!page) return;

  store.select(page.layers.filter((layer) => !layer.locked).map((layer) => layer.id));
}

/* ---------------------------------------------------------------
 * Pages
 * ------------------------------------------------------------ */

export function addPage() {
  // The product names its pages; the customer keeps exactly those.
  if (pagesFixed()) return;

  const store = useEditor.getState();
  const doc = store.doc;
  const current = store.page();

  if (!doc || !current) return;

  store.commit('Add page', (d: TrimboxDocument) => ({
    ...d,
    pages: d.pages.concat({
      id: newId('p'),
      name: `Page ${d.pages.length + 1}`,
      size: { ...current.size },
      // A new side of a round product is round. The shape comes from the
      // product, so every page of it has the same one - a square page in the
      // middle of a set of circles would be cut to a circle regardless, with
      // nothing on screen having said so.
      ...(current.cut ? { cut: current.cut } : {}),
      ...(current.round ? { round: true } : {}),
      background: null,
      layers: [],
    }),
  }));

  store.goToPage(doc.pages.length);
}

/* ---------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------ */

function without(layers: Layer[], ids: Set<string>): Layer[] {
  return layers
    .filter((layer) => !ids.has(layer.id))
    .map((layer) => (isGroup(layer) ? { ...layer, children: without(layer.children, ids) } : layer));
}

/** A deep copy with fresh ids, so a duplicate is genuinely a second object. */
function reId(layer: Layer): Layer {
  const copy = { ...layer, id: newId(layer.type[0] ?? 'l') };

  return isGroup(copy) ? { ...copy, children: copy.children.map(reId) } : copy;
}

const round = (mm: number) => Math.round(mm * 1000) / 1000;

/* ---------------------------------------------------------------
 * Lining things up
 * ------------------------------------------------------------ */

export type Edge = 'left' | 'centreX' | 'right' | 'top' | 'centreY' | 'bottom';

/** What the alignment is measured against. */
export type Against = 'selection' | 'page';

/**
 * Aligns everything selected to one edge.
 *
 * Two objects line up with each other; one lines up with the card. That is what
 * every design tool does and it is worth being explicit about, because the
 * alternative - a control that does nothing when one thing is selected - reads
 * as broken rather than as inapplicable.
 *
 * "The page" here is the trim, not the sheet. A customer aligning to the right
 * edge means the edge of the card they will hold, not the edge of the paper it
 * is printed on before it is cut.
 */
export function alignSelected(edge: Edge, against: Against = 'selection') {
  const store = useEditor.getState();
  const page = store.page();
  const chosen = store.selected().filter((layer) => !layer.locked);

  if (!page || chosen.length === 0) return;

  const to =
    against === 'page' || chosen.length === 1
      ? { x: 0, y: 0, w: page.size.w, h: page.size.h }
      : bounds(chosen);

  for (const layer of chosen) {
    const put = placed(layer, to, edge);

    store.updateLayer(layer.id, 'Align', (l) => ({ ...l, ...put }), { coalesce: 'align' });
  }
}

/** Where one layer goes, to sit on the given edge of a box. */
function placed(layer: Layer, to: { x: number; y: number; w: number; h: number }, edge: Edge) {
  switch (edge) {
    case 'left':
      return { x: round(to.x) };
    case 'centreX':
      return { x: round(to.x + (to.w - layer.w) / 2) };
    case 'right':
      return { x: round(to.x + to.w - layer.w) };
    case 'top':
      return { y: round(to.y) };
    case 'centreY':
      return { y: round(to.y + (to.h - layer.h) / 2) };
    default:
      return { y: round(to.y + to.h - layer.h) };
  }
}

/**
 * Spreads everything selected evenly.
 *
 * The two on the ends stay where they are and the rest are spaced between them,
 * which is what a customer means by "even" - moving the ends as well would
 * change the extent of the arrangement they have already positioned.
 *
 * Even *gaps*, not even centres. Objects of different widths spaced by their
 * centres leave visibly uneven space between them, and space is what the eye
 * reads.
 */
export function distributeSelected(axis: 'h' | 'v') {
  const store = useEditor.getState();
  const chosen = store.selected().filter((layer) => !layer.locked);

  if (chosen.length < 3) return;

  const along = axis === 'h' ? 'x' : 'y';
  const across = axis === 'h' ? 'w' : 'h';

  const order = chosen.slice().sort((a, b) => a[along] - b[along]);
  const first = order[0]!;
  const last = order[order.length - 1]!;

  const span = last[along] + last[across] - first[along];
  const taken = order.reduce((sum, layer) => sum + layer[across], 0);
  const gap = (span - taken) / (order.length - 1);

  let at = first[along] + first[across] + gap;

  for (const layer of order.slice(1, -1)) {
    const put = round(at);

    store.updateLayer(layer.id, 'Distribute', (l) => ({ ...l, [along]: put }), {
      coalesce: 'distribute',
    });

    at += layer[across] + gap;
  }
}

/** The box that just contains everything given. */
function bounds(layers: Layer[]) {
  const x = Math.min(...layers.map((l) => l.x));
  const y = Math.min(...layers.map((l) => l.y));

  return {
    x,
    y,
    w: Math.max(...layers.map((l) => l.x + l.w)) - x,
    h: Math.max(...layers.map((l) => l.y + l.h)) - y,
  };
}

/**
 * Mirrors everything selected.
 *
 * Each object about its own middle rather than the selection's, which is what
 * "flip" means when several things are chosen: the arrangement stays put and
 * each piece turns over. Mirroring the whole arrangement is what a customer
 * does by grouping first.
 */
export function flipSelected(axis: 'x' | 'y') {
  const store = useEditor.getState();
  const field = axis === 'x' ? 'flipX' : 'flipY';

  for (const layer of store.selected()) {
    if (layer.locked) continue;

    store.updateLayer(layer.id, 'Flip', (l) => ({ ...l, [field]: !l[field] }));
  }
}

/* ---------------------------------------------------------------
 * Pages
 * ------------------------------------------------------------ */

/** A copy of one page, with its layers given new ids. */
export function duplicatePage(index: number) {
  // The product names its pages; the customer keeps exactly those.
  if (pagesFixed()) return;

  const store = useEditor.getState();
  const doc = store.doc;
  const page = doc?.pages[index];

  if (!doc || !page) return;

  store.commit('Duplicate page', (current) => {
    const pages = current.pages.slice();

    pages.splice(index + 1, 0, {
      ...page,
      // A page's id is referred to by the product's own page list, so a copy
      // needs its own rather than a second page answering to the same name.
      id: newId('p'),
      name: `${page.name} copy`,
      layers: page.layers.map(reidentify),
    });

    return { ...current, pages };
  });

  store.goToPage(index + 1);
}

export function deletePage(index: number) {
  // The product names its pages; the customer keeps exactly those.
  if (pagesFixed()) return;

  const store = useEditor.getState();
  const doc = store.doc;

  // A design with no pages is not a design. The last one stays.
  if (!doc || doc.pages.length < 2 || !doc.pages[index]) return;

  store.commit('Delete page', (current) => ({
    ...current,
    pages: current.pages.filter((_page, at) => at !== index),
  }));

  store.goToPage(Math.max(0, index - 1));
}

export function movePage(index: number, to: number) {
  // The product names its pages; the customer keeps exactly those.
  if (pagesFixed()) return;

  const store = useEditor.getState();
  const doc = store.doc;

  if (!doc || !doc.pages[index] || to < 0 || to >= doc.pages.length || to === index) return;

  store.commit('Reorder pages', (current) => {
    const pages = current.pages.slice();
    const [moved] = pages.splice(index, 1);

    if (moved) pages.splice(to, 0, moved);

    return { ...current, pages };
  });

  store.goToPage(to);
}

export function renamePage(index: number, name: string) {
  // The product names its pages; the customer keeps exactly those.
  if (pagesFixed()) return;

  const store = useEditor.getState();
  const clean = name.trim().slice(0, 60);

  if (!clean) return;

  store.commit('Rename page', (current) => {
    const pages = current.pages.slice();
    const page = pages[index];

    if (!page) return current;

    pages[index] = { ...page, name: clean };

    return { ...current, pages };
  });
}

/** A layer and everything under it, with fresh ids. */
function reidentify(layer: Layer): Layer {
  const copy: Layer = { ...layer, id: newId(layer.type.charAt(0)) };

  if (copy.type === 'group') {
    return { ...copy, children: copy.children.map(reidentify) };
  }

  return copy;
}

/**
 * Turns a drawn path into a layer.
 *
 * The points arrive in millimetres on the page, because that is where they were
 * drawn; they are stored as proportions of the layer's own box, because that is
 * how a shape works and it is what makes resizing, flipping and turning apply
 * to a drawing exactly as they do to a rectangle.
 *
 * A drawn path is a shape, not a layer type of its own. Everything a shape can
 * do - gradients, outlines, shadows, the layer menu, the press pipeline - comes
 * with it and needed no changes at all.
 */
export function addDrawn(points: PathNode[], closed: boolean) {
  const store = useEditor.getState();
  const page = store.page();
  const box = extent(points);

  if (!page || !box || points.length < 2) return;

  const layer: Layer = {
    id: newId('s'),
    type: 'shape',
    name: 'Drawing',
    x: round(box.x),
    y: round(box.y),
    w: round(box.w),
    h: round(box.h),
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    shape: 'free',
    nodes: points.map((node) => toUnit(node, box)),
    closed,
    /*
     * An open path is a line and has no inside worth filling; a closed one is a
     * shape and does. Guessing the other way round leaves the customer with an
     * invisible drawing, or a black blob where they drew a squiggle.
     */
    fill: closed ? BLACK : { space: 'cmyk', value: [0, 0, 0, 0] },
    stroke: closed ? null : { colour: BLACK, width: 0.5, dash: [] },
    radius: 0,
    points: 5,
  };

  place(layer, 'Draw');
}

/**
 * New points for a drawing, in its own millimetres.
 *
 * Every point edit ends here: the box is refitted around the new points so the
 * selection, snapping and aligning keep describing what is actually drawn, and
 * the layer is moved so nothing that was not touched moves on the page.
 *
 * @param coalesce A key shared by every step of one gesture, so a drag is one undo.
 */
export function setPath(id: string, nodes: PathNode[], label = 'Edit points', coalesce?: string) {
  const store = useEditor.getState();

  store.updateLayer(
    id,
    label,
    (layer) => {
      if (layer.type !== 'shape' || layer.shape !== 'free' || nodes.length < 2) return layer;

      return { ...layer, ...rebase(layer, nodes) };
    },
    coalesce ? { coalesce } : undefined
  );
}

/* ---------------------------------------------------------------
 * Effects, curves and text on a line
 * ------------------------------------------------------------ */

/**
 * An effect on a text or a shape, or none.
 *
 * @param coalesce A key shared by one gesture - dragging a slider - so the
 *   whole drag is one undo step.
 */
export function setEffect(id: string, effect: Effect | null, coalesce?: string) {
  useEditor.getState().updateLayer(
    id,
    effect ? 'Effect' : 'Remove effect',
    (layer) => {
      if (layer.type === 'text') return { ...layer, effect };

      // An effect replaces a shape's older hard shadow for good - otherwise
      // removing the effect would bring back a shadow nobody asked for.
      if (layer.type === 'shape') return { ...layer, effect, shadow: null };

      return layer;
    },
    coalesce ? { coalesce } : undefined
  );
}

/**
 * An advanced look: its font, its colour and its effect, as one step.
 *
 * The font is only changed when the shop has it - a preset whose font is
 * missing is not offered at all, but a design opened on another machine must
 * not be rewritten into a font that is not there. Capitals, where the look
 * depends on them, are applied to the text itself, because that is what the
 * customer then sees and edits; undo puts them back.
 */
export function applyPreset(id: string, preset: Preset, intensity = 50) {
  const has = fontFamilies().includes(preset.font.family);

  useEditor.getState().updateLayer(id, preset.name, (layer) => {
    if (layer.type !== 'text') return layer;

    const next: TextLayer = {
      ...layer,
      fill: preset.fill,
      effect: preset.effect(intensity),
      text: preset.upper ? layer.text.toUpperCase() : layer.text,
    };

    if (has) next.font = { ...layer.font, family: preset.font.family, weight: preset.font.weight, style: 'normal' };

    return next;
  });
}

/**
 * Bends text along an arc, and refits its box around what the bend covers.
 *
 * The box is what the customer selects, snaps and aligns, so it has to follow
 * the text: an arch is taller than the line it was. The middle of the box stays
 * put, so the text bends in place rather than wandering.
 */
export function setCurve(id: string, curve: number, coalesce?: string) {
  useEditor.getState().updateLayer(
    id,
    'Curve',
    (layer) => {
      if (layer.type !== 'text') return layer;

      const bent: TextLayer = { ...layer, curve: Math.round(Math.max(-100, Math.min(100, curve))) };

      return fitted(bent, layer);
    },
    coalesce ? { coalesce } : undefined
  );
}

/**
 * The same text with its box fitted to what it covers, centred where the old
 * box was. Straight text gets its column back at the width it now needs.
 */
function fitted(next: TextLayer, before: TextLayer): TextLayer {
  const laid = layoutFor(next);

  if (!laid) return next;

  const cover = placeText({ ...next, w: before.w, h: before.h }, laid).extent;
  const margin = 0.5;
  const w = round(Math.max(1, cover.w + margin * 2));
  const h = round(Math.max(1, cover.h + margin * 2));

  return {
    ...next,
    x: round(before.x + before.w / 2 - w / 2),
    y: round(before.y + before.h / 2 - h / 2),
    w,
    h,
  };
}

/**
 * Lays a text along a drawing: the text takes the drawing's line and its box,
 * and the drawing itself goes, as it does in every tool that offers this - the
 * line becomes the text's, rather than a second object to keep in step.
 * One undo puts both back.
 */
export function textOnPath(textId: string, drawingId: string): boolean {
  const store = useEditor.getState();
  const page = store.page();
  const text = page ? findLayer(page.layers, textId) : null;
  const drawing = page ? findLayer(page.layers, drawingId) : null;

  if (!text || text.type !== 'text' || !drawing || drawing.type !== 'shape' || drawing.shape !== 'free') return false;
  if (!drawing.nodes || drawing.nodes.length < 2) return false;

  const line = drawing;

  store.commit('Text on a line', (doc) => {
    const pages = doc.pages.slice();
    const index = store.pageIndex;
    const onPage = pages[index];

    if (!onPage) return doc;

    const layers = onPage.layers
      .filter((layer) => layer.id !== line.id)
      .map((layer) =>
        layer.id === textId && layer.type === 'text'
          ? {
              ...layer,
              x: line.x,
              y: line.y,
              w: line.w,
              h: line.h,
              rotation: line.rotation,
              flipX: line.flipX,
              flipY: line.flipY,
              path: { nodes: line.nodes ?? [], closed: !!line.closed },
            }
          : layer
      );

    pages[index] = { ...onPage, layers };

    return { ...doc, pages };
  });

  store.select([textId]);

  return true;
}

/** Takes a text off its line, back into a straight box around what it covered. */
export function offPath(id: string) {
  useEditor.getState().updateLayer(id, 'Straighten text', (layer) => {
    if (layer.type !== 'text' || !layer.path) return layer;

    return fitted({ ...layer, path: null, curve: 0 }, layer);
  });
}

/**
 * Why a shape cannot become a drawing, or null when it can.
 *
 * A drawing is one path. A ring or a frame is two - the outline and the hole -
 * and flattening them into one would fill the hole in.
 */
export function cannotConvert(layer: Layer): string | null {
  if (layer.type !== 'shape') return 'Only shapes have points.';
  if (layer.shape === 'free') return null;

  return fromPath(shapePath(layer)) ? null : 'This shape has a hole in it, so its points cannot be edited.';
}

/**
 * Turns a library shape into a drawing, so its points can be moved.
 *
 * The shape's corner radius and point count are baked in at the moment of
 * converting, which is the trade: the rounded rectangle stops being adjustable
 * as a rounded rectangle and becomes adjustable point by point. One undo step
 * puts it back.
 */
export function convertToPath(id: string): boolean {
  const store = useEditor.getState();
  const page = store.page();
  const layer = page ? findLayer(page.layers, id) : null;

  if (!layer || layer.type !== 'shape' || layer.locked) return false;
  if (layer.shape === 'free') return true;

  const path = fromPath(shapePath(layer));

  if (!path) return false;

  const lineOnly = !!shapeDef(layer.shape).strokeOnly;

  store.updateLayer(id, 'Convert to points', (l) =>
    l.type === 'shape'
      ? {
          ...l,
          ...rebase(l, path.nodes),
          shape: 'free',
          closed: path.closed,
          /*
           * A line's colour lives in its stroke, and its fill was never shown -
           * or was, standing in for a stroke it did not have. As a drawing the
           * fill would suddenly count, so the line gets the stroke it was
           * printed with (the press's own default for one without) and an empty
           * fill, rather than a sliver of nothing filled in black.
           */
          fill: lineOnly ? { space: 'cmyk', value: [0, 0, 0, 0] } : l.fill,
          stroke: lineOnly ? (l.stroke ?? { colour: firstColour(l.fill), width: 0.25, dash: [] }) : l.stroke,
        }
      : l
  );

  return true;
}

/**
 * Square or rounded, from the answers the product offers.
 *
 * The choice is the product's - it is priced there, and it goes back to the
 * product page with the design - so this records which answer, not only the
 * radius, and the product page is set to match when the customer returns.
 */
export function setCorners(answer: { key: string; label: string; radius: number }) {
  useEditor.getState().commit('Corners', (doc) => {
    const was = doc.corners;

    if (was && was.choice === answer.key && was.radius === answer.radius) return doc;

    return { ...doc, corners: { radius: answer.radius, choice: answer.key, label: answer.label } };
  });
}

/**
 * How many printed pages the booklet has, from the answers the product offers.
 *
 * Pages are added and removed as spreads, before the back cover, because that
 * is where a catalogue grows: nobody adds a page after the back. Pages with
 * something on them are never thrown away silently - the customer is told and
 * decides.
 *
 * @returns The problem to show, or null when it worked.
 */
export function setPageCount( answer: { key: string; label: string; count: number } ): string | null {
  const store = useEditor.getState();
  const doc = store.doc;

  if ( ! doc || ! doc.booklet ) return null;

  const wanted = answer.count;

  if ( wanted < 4 || wanted % 4 !== 0 ) return 'A booklet is made from folded sheets, so it comes in fours.';

  const cover = doc.pages[ 0 ];
  const back = doc.pages[ doc.pages.length - 1 ];

  if ( ! cover || ! back || doc.pages.length < 2 ) return null;

  const insides = doc.pages.slice( 1, -1 );
  const spreads = ( wanted - 2 ) / 2;

  if ( spreads < insides.length ) {
    const dropping = insides.slice( spreads );

    if ( dropping.some( ( page ) => page.layers.length > 0 || page.background ) ) {
      return 'The pages that would go have artwork on them. Clear them first, or keep the pages you have.';
    }
  }

  const size = { w: cover.size.w * 2, h: cover.size.h };
  const kept = insides.slice( 0, spreads );

  while ( kept.length < spreads ) {
    kept.push( {
      id: newId( 'p' ),
      name: '',
      size,
      background: null,
      layers: [],
      spread: true,
    } );
  }

  store.commit( `${ wanted } pages`, ( current ) => {
    const pages = [ cover, ...kept, back ];
    const next = {
      ...current,
      pages,
      booklet: { count: wanted, choice: answer.key, label: answer.label },
    };

    // The names follow the numbering, so a saved design reads the same on the
    // order screen as it did in the editor.
    return {
      ...next,
      pages: next.pages.map( ( page, index ) => ( { ...page, name: pageLabel( next, index ) } ) ),
    };
  } );

  // Nothing to say when the count did not actually change.
  return printedPages( useEditor.getState().doc ?? doc ) === wanted ? null : 'The pages could not be changed.';
}

/**
 * Puts one of the shop's drawings on the card.
 *
 * A drawing is read into outlines here, in the browser, and lands as paths the
 * customer can recolour and the press can separate. A picture is copied into
 * the design first - or kept in the browser, for a visitor who has not signed
 * in yet - and placed like any other photograph.
 *
 * @returns The problem to show, or null when it worked.
 */
export async function addGraphic( item: { id: number; name: string; url: string; kind: 'svg' | 'image' } ): Promise<string | null> {
  const store = useEditor.getState();
  const page = store.page();

  if ( ! page ) return null;

  if ( 'svg' === item.kind ) {
    try {
      const text = await ( await fetch( item.url, { credentials: 'same-origin' } ) ).text();
      const { layer, ratio } = importSvg( text, item.name );

      // A third of the card's width, and never taller than it.
      const width = Math.min( page.size.w / 3, page.size.h * 0.8 * ratio );
      const height = width / ( ratio || 1 );

      const placed: Layer = {
        ...layer,
        ...base( 'graphic', layer.name, page ),
        type: 'graphic',
        w: round( width ),
        h: round( height ),
        x: round( ( page.size.w - width ) / 2 ),
        y: round( ( page.size.h - height ) / 2 ),
      } as Layer;

      const now = useEditor.getState();

      now.commit( 'Add a graphic', ( doc ) => {
        const target = doc.pages[ now.pageIndex ];

        if ( ! target ) return doc;

        const pages = doc.pages.slice();

        pages[ now.pageIndex ] = { ...target, layers: target.layers.concat( placed ) };

        return { ...doc, pages };
      } );

      useEditor.getState().select( [ placed.id ] );

      return null;
    } catch ( error ) {
      return error instanceof Error ? error.message : 'That drawing could not be used.';
    }
  }

  try {
    if ( signedIn() ) {
      const asset = await api.placeGraphic( await ensureStored(), item.id );

      registerAssets( [ asset ] );
      placeAsset( asset );
    } else {
      // A visitor keeps it in their own browser, like any picture of theirs,
      // and it goes to the shop with the rest when they sign in.
      const file = await ( await fetch( item.url, { credentials: 'same-origin' } ) ).blob();

      const asset = await keepPicture( await printableFile( new File( [ file ], item.name, { type: file.type } ) ) );

      registerAssets( [ asset ] );
      placeAsset( asset );
    }

    return null;
  } catch ( error ) {
    return error instanceof Error ? error.message : 'That picture could not be added.';
  }
}
